import React, { useState, useMemo } from "react";
import "./Feedback.css";
import goodStyle from "../../assets/images/goodStyle.png";
import worst from "../../assets/images/worst.png";
import fine from "../../assets/images/fine.png";
import bad from "../../assets/images/bad.png";
import excellent from "../../assets/images/excellent.png";
import { useDispatch } from "react-redux";
import { env } from "../../environment";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTranslationContext } from "../../contexts/TranslationContext";
import { sessionFeedback } from "../../store/apiActions";
import { encrypted } from "../../utils/Utility";

const FeedbackPopup = ({ onClose }) => {
  const [rating, setRating] = useState(5);
  const [activeEmoji, setActiveEmoji] = useState(5);
  const [comment, setComment] = useState("");
  const { translations } = useTranslationContext();
  const dispatch = useDispatch();

  // Translation variables
  const worstText = translations["worst"] || "Worst";
  const badText = translations["bad"] || "Bad";
  const fineText = translations["fine"] || "Fine";
  const goodText = translations["good"] || "Good";
  const excellentText = translations["excellent"] || "Excellent";
  const feedbackTitle = translations["feedback_title"] || "Give us a feedback!";
  const feedbackDescription = translations["feedback_description"] || "We'd love to hear your thoughts on Chat NPP! Your feedback helps us improve the app.";
  const feedbackCommentPrompt = translations["feedback_comment_prompt"] || "Add your comments";
  const feedbackSendText = translations["feedback_send"] || "Send";
  const feedbackCancelText = translations["feedback_cancel"] || "Cancel";
  const feedbackSuccessText = translations["feedback_success"] || "Feedback submitted successfully";
  const feedbackFailureText = translations["feeback_failure"] || "Failed to submit feedback";

  const emojis = useMemo(
    () => [
      { src: worst, text: worstText, value: 1 },
      { src: bad, text: badText, value: 2 },
      { src: fine, text: fineText, value: 3 },
      { src: goodStyle, text: goodText, value: 4 },
      { src: excellent, text: excellentText, value: 5 },
    ],
    [worstText, badText, fineText, goodText, excellentText]
  );

  // Compute the slider background dynamically
  const sliderBackground = useMemo(() => {
    const percentage = ((rating - 1) / 4) * 100;
    return `linear-gradient(to right, rgba(231, 140, 64, 1) 0%, rgba(231, 140, 64, 1) ${percentage}%, rgba(255, 219, 200, 1) ${percentage}%, rgba(255, 219, 200, 1) 100%)`;
  }, [rating]);

  const handleSliderChange = (event) => {
    const value = Number(event.target.value);
    if (value !== rating) {
      setRating(value);
      setActiveEmoji(value);
    }
  };

  const handleEmojiClick = (value) => {
    if (activeEmoji !== value) {
      setActiveEmoji(value);
      setRating(value);
    }
  };

  const handleFeedbackSubmit = () => {
    let sessionData = encrypted.getDecryptedItem(
      "startSession");
    const sessionId = sessionData?.session_id || "";
    const userId = sessionData?.user_id || ""
    let requestBody = {
      user_id: userId,
      session_id: sessionId,
      model_name: sessionData?.model_name,
      temperature: sessionData?.temperature,
      top_p: sessionData?.top_p,
      search_index: sessionData?.search_index,
      embedding_name: sessionData?.embedding_name,
      search_strictness: sessionData?.search_strictness,
      search_top_k: sessionData?.search_top_k,
      max_tokens: sessionData?.max_tokens,
      api_version: sessionData?.api_version,
      search_service: sessionData?.search_service,
      tipo_chat: sessionData?.tipo_chat,
      idioma_3l: sessionData?.idioma_3l,
      language: sessionData?.language,
      country: sessionData?.country,
      queries: sessionData?.queries,
      responses: sessionData?.responses,
      feedback: JSON.stringify(rating),
      feedback_comment: comment,
      feedback_session: JSON.stringify(rating),
      feedback_comment_session: comment,
    };

    dispatch(
      sessionFeedback(
        `${env.baseURL}/update-feedback-session`,
        requestBody
      )
    )
      .then(() => {
        toast.success(feedbackSuccessText);
        encrypted.setEncryptedItem(
          "feedbackSubmitted", "true");
        onClose();
      })
      .catch((error) => {
        console.error("Feedback submission error:", error);
        toast.error(feedbackFailureText);
      });
  };

  return (
    <div className="feedback-container">
      <div className="feedback-popup">
        <div className="give-feedback">{feedbackTitle}</div>
        <div className="feedback-text">{feedbackDescription}</div>

        <div className="rating-container">
          <div className="emojis">
            {emojis.map((emoji) => (
              <div
                key={emoji.value}
                className={`emoji-item ${activeEmoji === emoji.value ? "active" : ""}`}
                onClick={() => handleEmojiClick(emoji.value)}
              >
                <img src={emoji.src} alt={emoji.text} />
                <span>{emoji.text}</span>
              </div>
            ))}
          </div>

          <div className="slider">
            <input
              type="range"
              min="1"
              max="5"
              value={rating}
              onChange={handleSliderChange}
              style={{ background: sliderBackground }}
            />
          </div>
        </div>

        <textarea
          placeholder={feedbackCommentPrompt}
          className="feedback-textarea"
          value={comment}
          maxLength={5000}
          onChange={(e) => {
            setComment(e.target.value);
          }}
        />

        <div className="button-section">
          <button onClick={handleFeedbackSubmit} className="feedback-send-button">
            {feedbackSendText}
          </button>
          <button onClick={onClose} className="feedback-close-button">
            {feedbackCancelText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeedbackPopup;